import { Button, CircularProgress, Divider, Grid, IconButton, InputAdornment, makeStyles, Snackbar, TextField } from '@material-ui/core'
import HelpIcon from '@material-ui/icons/Help'
import { Alert } from '@material-ui/lab'
import { Color } from '@material-ui/lab/Alert'
import { get } from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import http from '../../http'
import { OrderContext } from '../../OrderContext'
import { GetKey } from '../../OrderContext/OrderReducer'
import type { MaterialBatchInvoice } from '../../OrderContext/types'
import { CameraScannerButton, ReturnSearchContext } from './index'
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined'
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined'

const useStyles = makeStyles(theme => ({
    root: {
        marginTop: 15,
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '100%'
        }
    },
    adornmentDivider: {
        height: 26,
    },
    iconButton: {
        padding: 10,
    },
    input: {
        background: theme.palette.background.paper
    }
}))

type SearchTextInputProps = { value?: string, onChange: React.Dispatch<string>, label: string }

const SearchTextInput: React.FC<SearchTextInputProps> = ({ value, onChange, label }) => {
    const { setDisplayBarcodeHelp } = useContext(ReturnSearchContext)
    const classes = useStyles()

    return <TextField
        required
        variant='outlined'
        label={label}
        value={value}
        onChange={e => onChange(e.target.value.toUpperCase())}
        InputProps={{
            endAdornment: (
                <InputAdornment position="end">
                    <Divider className={classes.adornmentDivider} orientation="vertical" />
                    <IconButton tabIndex={-1} className={classes.iconButton} onClick={() => setDisplayBarcodeHelp(true)}>
                        <HelpIcon />
                    </IconButton>
                </InputAdornment>
            ),
        }}
    />
}

const SearchForm = () => {
    const classes = useStyles()
    const { material, setMaterial, batch, setBatch, quantity, resetSearch } = useContext(ReturnSearchContext)
    const { dispatch, order, orderType, customer } = useContext(OrderContext)
    const [open, setOpen] = useState<boolean>(false)
    const [message, setMessage] = useState<[Color, string]>()
    const [loading, setLoading] = useState(false)
    const [hasCamera, setHasCamera] = useState<boolean>(false)
    const { t } = useTranslation()

    useEffect(() => {
        navigator.mediaDevices.enumerateDevices().then(devices => setHasCamera(devices.some(d => d.kind === 'videoinput')))
    }, [])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setMessage(undefined)

        if (material && batch && order[GetKey(material, batch)]) {
            const mb = order[GetKey(material, batch)]

            dispatch({
                type: 'ChangeQuantity',
                material: material,
                batch: batch,
                quantity: quantity + (mb.quantity || 0)
            })

            quantity + (mb.quantity || 0) > mb.maxQuantity
                ? setMessage(['warning', t('added_item_to_list') + ' ' + t('max_quantity_exceeded')])
                : setMessage(['success', t('added_item_to_list')])

            resetSearch()
            setOpen(true)
        } else {
            setLoading(true)

            http.get<MaterialBatchInvoice[]>(`/api/Invoice?material=${encodeURIComponent(material)}&batch=${encodeURIComponent(batch)}`)
                .then(({ data }) => {
                    if (!data) throw new Error(t('no_invoice_found'))
                    if (data.length && get(data[0], 'status')) throw new Error(get(data[0], 'error'))
                    if (data.length) {
                        if (data.every(i => i.exclusionMaterial)) {
                            throw new Error(t('material_excluded_from_returns'))
                        }

                        if (orderType === 'Return' && data.every(i => !i.return)) {
                            throw new Error(t('return_policy_expired'))
                        }

                        if (orderType === 'Exchange' && data.every(i => !i.exchange)) {
                            throw new Error(t('exchange_policy_expired', { days: customer?.salesOrg === '0013' ? 365 : 730 }))
                        }
                    }

                    dispatch({ type: 'AddMaterialBatch', invoices: data, quantity: quantity, orderType })

                    quantity > data.filter(r => r.exchange || r.return).reduce((acc, val) => acc + parseInt(val.quantity), 0)
                        ? setMessage(['warning', t('added_item_to_list') + ' ' + t('max_quantity_exceeded')])
                        : setMessage(['success', t('added_item_to_list')])

                    resetSearch()
                })
                .catch(e => { setMessage(['error', e.message]); resetSearch(); })
                .finally(() => { setLoading(false); setOpen(true); })
        }
    }

    return (
        <form onSubmit={handleSubmit} className={classes.root}>
            <Grid container alignItems='center' spacing={3}>
                {
                    hasCamera && <>
                        <Grid item md={3} sm={12} xs={12}>
                            <CameraScannerButton />
                        </Grid>

                        <Grid item md={1} sm={12} xs={12} style={{ textAlign: 'center' }}>
                            <p>{t('or_manual_input')}:</p>
                        </Grid>
                    </>
                }

                <Grid item md={hasCamera ? 4 : 6} sm={6} style={{ width: '100%' }}>
                    <SearchTextInput value={material} onChange={setMaterial} label={t('material_number')} />
                </Grid>
                <Grid item md={hasCamera ? 4 : 6} sm={6} style={{ width: '100%' }}>
                    <SearchTextInput value={batch} onChange={setBatch} label={t('lot_number')} />
                </Grid>

                <Grid item xs={12}>
                    <Button size='large' variant="contained" color="primary" type='submit' style={{ float: 'right' }} disabled={loading}>{t('add_item')} {loading ? <CircularProgress size='1.5rem' style={{ marginLeft: '1rem' }} /> : null}</Button>
                    <Button size='large' variant="contained" color="secondary" onClick={resetSearch} style={{ float: 'right', marginRight: 15 }} disabled={loading}>{t('clear')}</Button>
                </Grid>

                <Snackbar
                    open={open}
                    autoHideDuration={6000}
                    onClose={() => setOpen(false)}
                >
                    {message && <Alert
                        icon={message[0] === 'error' ? <ErrorOutlineOutlinedIcon fontSize='large' /> : <CheckCircleOutlineOutlinedIcon fontSize='large' />}
                        style={{ padding: 30, fontSize: '1.35em' }}
                        onClose={() => setOpen(false)}
                        severity={message[0]}
                    >
                        {message[1]}
                    </Alert>}
                </Snackbar>
            </Grid>
        </form>
    )
}

export default SearchForm
